import { ComponentIds, HierarchyUtils, } from "@inter-ikea-core/core";
import { COMPONENT_IDS } from "@inter-ikea-storagetwo/range/dist/storage2ComponentIDs";
import { NO_MOUNTING_RAILS, NO_UPRIGHTS } from "../constants/errors";
import { getWallConnectorKey, MountingRailConnectorService, } from "./connectorHelpers";
import { MountingRailConnectionService, } from "./ConnectorService";
import { MOUNTING_RAIL_RIGHT_SIDE_UPRIGHT_CONNECTORS, MOUNTING_RAIL_TO_UPRIGHT, MOUNTING_RAIL_TO_UPRIGHT_LEFT, SIZE_TO_HOST_CONNECTOR_MAP, UPRIGHT_TO_INSERT, UPRIGHT_TO_MOUNTING_RAIL_LEFT, } from "../constants/connectorKeys";
import { indexMountingRailsInSolution } from "./mountingRailHelpers";
import RequestMergeComponent from "../components/RequestsMergeComponent";
import { getEntityHeight, getRightUprightsOffsetFromLeft, } from "../functions/interactiveBoaxelSection";
import { createEntity, getEntityProductRef } from "../functions/MountingRails";
import { findHoleByConnectorKey } from "../functions/HoleOccupation";
import { getGuestConnections } from "@inter-ikea-storagetwo/range/dist/functions/componentGetters";
import { CHILD_IS_ORPHAN, WIDTH_NOT_SUPPORTED, } from "@inter-ikea-storagetwo/range/dist/constants/errors";
/**
 * Checks if an entity is most part of most left section
 * @param entity
 */
export const entityBelongsToMostLeftSection = (entity) => {
    var _a;
    const mountingRail = entity.hasComponent(COMPONENT_IDS.MOUNTING_RAIL_COMPONENT)
        ? entity
        : HierarchyUtils.findWithComponentInUpwardsHierarchy(entity, COMPONENT_IDS.MOUNTING_RAIL_COMPONENT);
    if (!mountingRail)
        return false;
    const isChildOfUpright = (_a = HierarchyUtils.parent(mountingRail)) === null || _a === void 0 ? void 0 : _a.hasComponent(COMPONENT_IDS.UPRIGHT_COMPONENT);
    return !isChildOfUpright;
};
/**
 * Returns the appropriate host connector key based on the provided mounting rail width
 *
 * @param mountingRailWidth
 * @returns {IHostConnectorKey} The corresponding host connector key
 * @throws {Error} if no matching connector key is found
 */
export const getStandardRightMountingRailConnectorKeyBasedOnWidth = (mountingRailWidth) => {
    let connectorKey = SIZE_TO_HOST_CONNECTOR_MAP[mountingRailWidth];
    //If not found, try rounding down to nearest 10 since adjustable width is not accurate.
    if (!connectorKey) {
        const roundedWidth = Math.floor(mountingRailWidth / 10) * 10;
        connectorKey = SIZE_TO_HOST_CONNECTOR_MAP[roundedWidth];
    }
    if (!connectorKey)
        throw new Error(WIDTH_NOT_SUPPORTED);
    return connectorKey;
};
/**
 * Determines if the given entity's the most left upright in the solution it belongs
 *
 * @throws {Error} Throws an error if the parent lacks a point connector.
 * @returns {boolean} Returns true if the entity's most left connector is upright, else false.
 * @param upright
 */
export const isMostLeftUpright = (upright) => {
    const guestConnection = getGuestConnections(upright)[0];
    return guestConnection.hostConnector.key === UPRIGHT_TO_MOUNTING_RAIL_LEFT;
};
/**
 * Retrieves the right mounting rail based on the given entity's left upright position.
 *
 * @param {Entity} entity - The entity for which the right mounting rail is needed.
 * @param allowOrhpan
 * @returns {Entity} - The corresponding mounting rail entity.
 * @throws {Error} Throws an error if no mounting rail found.
 */
export const getRightMountingRailBasedOnLeftUpright = (entity, allowOrhpan = false) => {
    const isMostLeft = isMostLeftUpright(entity);
    const mountingRail = isMostLeft
        ? HierarchyUtils.parent(entity)
        : HierarchyUtils.children(entity).find((entity) => {
            return entity.hasComponent(COMPONENT_IDS.MOUNTING_RAIL_COMPONENT);
        });
    if (!mountingRail && !allowOrhpan) {
        throw new Error(CHILD_IS_ORPHAN);
    }
    return mountingRail || false;
};
export const hostingUprightIsPartOfSection = (upright) => {
    const mountingRail = isMostLeftUpright(upright)
        ? HierarchyUtils.parent(upright)
        : HierarchyUtils.children(upright).find((child) => child.hasComponent(COMPONENT_IDS.MOUNTING_RAIL_COMPONENT));
    if (!mountingRail)
        return false;
    const { leftUpright, rightUpright } = getUprights(mountingRail);
    return leftUpright && rightUpright;
};
/**
 * Retrieves the 'uprights' component from the section parts of a given host connector entity.
 *
 * @param {Connection} params.hostConnectorEntity - The host connector entity from which the uprights component is derived.
 * @returns {Uprights} The 'uprights' component of the section parts.
 * @throws Will throw an error if the section parts component is broken or missing.
 * @param upright
 */
export const getSectionUprightsByUpright = (upright) => {
    const mountingRail = getRightMountingRailBasedOnLeftUpright(upright);
    if (!mountingRail)
        throw new Error(CHILD_IS_ORPHAN);
    return getUprights(mountingRail);
};
/**
 * Gets a mounting rails left and right uprights and return it as an object.
 *
 * @param mountingRail
 */
export const getUprights = (mountingRail) => {
    var _a, _b;
    const leftUpright = [
        (_a = MountingRailConnectorService[UPRIGHT_TO_MOUNTING_RAIL_LEFT](mountingRail)) === null || _a === void 0 ? void 0 : _a.guestConnectorEntity,
        (_b = MountingRailConnectorService[MOUNTING_RAIL_TO_UPRIGHT_LEFT](mountingRail)) === null || _b === void 0 ? void 0 : _b.hostConnectorEntity,
    ].reduce((acc, curr) => curr || acc, null);
    const rightUpright = MOUNTING_RAIL_RIGHT_SIDE_UPRIGHT_CONNECTORS.reduce((acc, curr) => {
        var _a;
        return ((_a = MountingRailConnectorService[curr](mountingRail)) === null || _a === void 0 ? void 0 : _a.guestConnectorEntity) || acc;
    }, null);
    return { leftUpright, rightUpright };
};
export const hasUprights = (mountingRail) => {
    const { leftUpright, rightUpright } = getUprights(mountingRail);
    return !!leftUpright && !!rightUpright;
};
export const getLeftUpright = (mountingRail) => {
    const { leftUpright } = getUprights(mountingRail);
    if (!leftUpright)
        throw new Error(NO_UPRIGHTS);
    return leftUpright;
};
//Perform connections in correct order
const performConnections = (core, sourceMountingRail, targetMountingRail, leftUpright, standardKey, mountingRailConnectionService, exchangeToTallerUpright) => {
    const wallKey = getWallConnectorKey(sourceMountingRail);
    const wall = HierarchyUtils.parent(sourceMountingRail);
    if (!wall)
        throw new Error(CHILD_IS_ORPHAN);
    const chosenUpright = exchangeToTallerUpright
        ? createEntity(exchangeToTallerUpright, core.services.productManager)
        : leftUpright;
    sourceMountingRail.removeComponent(ComponentIds.DRAGGABLE_COMPONENT_ID);
    mountingRailConnectionService.disconnect(sourceMountingRail);
    mountingRailConnectionService.disconnect(leftUpright);
    const children = HierarchyUtils.children(leftUpright)
        .map((child) => {
        return (child.hasComponent(COMPONENT_IDS.INSERT) &&
            mountingRailConnectionService.disconnect(child));
    })
        .filter((c) => !!c);
    exchangeToTallerUpright && leftUpright.dispose();
    mountingRailConnectionService
        .connect(sourceMountingRail)
        .to(chosenUpright)
        .atConnector(MOUNTING_RAIL_TO_UPRIGHT);
    mountingRailConnectionService
        .connect(chosenUpright)
        .to(targetMountingRail)
        .atConnector(standardKey);
    children === null || children === void 0 ? void 0 : children.forEach((connection) => {
        mountingRailConnectionService
            .connect(connection.guestConnectorEntity)
            .to(chosenUpright)
            .atConnector(connection.hostConnector.key);
    });
    mountingRailConnectionService
        .connect(targetMountingRail)
        .to(wall)
        .atConnector(wallKey);
};
export const connectSectionFromSide = (core) => (draggedMountingRail, offeringMountingRail, addFromLeft) => {
    const mountingRailConnectionService = MountingRailConnectionService(core);
    //Since it's the root mounting rail parent we're dragging we need to make sure
    //it is the last mountingrail in the combination to be able to snap things correctly.
    const lastDraggedMountingRailInCombination = Object.values(indexMountingRailsInSolution(draggedMountingRail)).at(-1);
    const lastOfferingMountingRailInCombination = Object.values(indexMountingRailsInSolution(offeringMountingRail)).at(-1);
    const newFirstMountingRail = addFromLeft
        ? lastDraggedMountingRailInCombination
        : offeringMountingRail;
    if (!lastDraggedMountingRailInCombination ||
        !lastOfferingMountingRailInCombination ||
        !newFirstMountingRail)
        throw new Error("Combination hierarchy faulty");
    const rightToBeDisposedUpright = getUprights(addFromLeft
        ? lastDraggedMountingRailInCombination
        : lastOfferingMountingRailInCombination).rightUpright;
    const leftKeptUpright = getUprights(addFromLeft
        ? lastOfferingMountingRailInCombination
        : lastDraggedMountingRailInCombination).leftUpright;
    if (!rightToBeDisposedUpright || !leftKeptUpright)
        return;
    const uprightRef = getEntityProductRef(rightToBeDisposedUpright);
    const lastDraggedMountingRailWidth = getRightUprightsOffsetFromLeft(lastDraggedMountingRailInCombination, rightToBeDisposedUpright);
    const lastOfferingMountingRailWidth = getRightUprightsOffsetFromLeft(lastOfferingMountingRailInCombination, rightToBeDisposedUpright);
    const exchangeToTallerUpright = getEntityHeight(rightToBeDisposedUpright) >
        getEntityHeight(leftKeptUpright)
        ? uprightRef
        : "";
    mountingRailConnectionService.disconnect(rightToBeDisposedUpright);
    rightToBeDisposedUpright.dispose();
    if (addFromLeft) {
        const standardKey = getStandardRightMountingRailConnectorKeyBasedOnWidth(lastDraggedMountingRailWidth);
        offeringMountingRail.addComponent(new RequestMergeComponent());
        connectSectionFromLeft(core, lastDraggedMountingRailInCombination, offeringMountingRail, mountingRailConnectionService, standardKey, exchangeToTallerUpright);
        return;
    }
    draggedMountingRail.addComponent(new RequestMergeComponent());
    const standardKey = getStandardRightMountingRailConnectorKeyBasedOnWidth(lastOfferingMountingRailWidth);
    connectSectionFromRight(core, draggedMountingRail, offeringMountingRail, mountingRailConnectionService, standardKey, exchangeToTallerUpright);
};
//Dragged section should attach to someones left side
const connectSectionFromLeft = (core, draggedMountingRail, offeringMountingRail, mountingRailConnectionService, standardKey, exchangeToTallerUpright) => {
    const { leftUpright } = getUprights(offeringMountingRail);
    if (!leftUpright)
        throw new Error(NO_UPRIGHTS);
    performConnections(core, offeringMountingRail, draggedMountingRail, leftUpright, standardKey, mountingRailConnectionService, exchangeToTallerUpright);
    offeringMountingRail.removeComponent(RequestMergeComponent.id);
};
//Dragged section should attach to someones right side
const connectSectionFromRight = (core, draggedMountingRail, offeringMountingRail, mountingRailConnectionService, standardKey, exchangeToTallerUpright) => {
    const { leftUpright } = getUprights(draggedMountingRail);
    if (!leftUpright)
        throw new Error(NO_UPRIGHTS);
    performConnections(core, draggedMountingRail, offeringMountingRail, leftUpright, standardKey, mountingRailConnectionService, exchangeToTallerUpright);
    draggedMountingRail.removeComponent(RequestMergeComponent.id);
};
/**
 * Place new mounting rail of choice to the left side available section.
 *
 * @param core
 * @param currentlyFirstMountingRail
 * @param newMountingRail
 * @param uprightRef
 */
export const addSectionFromLeftSide = (core, currentlyFirstMountingRail, newMountingRail, uprightRef) => {
    const mountingRailConnectionService = MountingRailConnectionService(core);
    if (!newMountingRail)
        throw new Error(NO_MOUNTING_RAILS);
    const newLeftUpright = core.services.productManager.createEntity(uprightRef);
    if (!newLeftUpright)
        throw new Error(NO_UPRIGHTS);
    mountingRailConnectionService
        .connect(newLeftUpright)
        .to(newMountingRail)
        .atConnector(UPRIGHT_TO_MOUNTING_RAIL_LEFT);
    connectSectionFromSide(core)(newMountingRail, currentlyFirstMountingRail, true);
};
export const getDiagonalBracketConnectorKey = (uprightConnectorKey, adjustableConnectorKey) => {
    const uprightNumber = findHoleByConnectorKey(uprightConnectorKey);
    const insertNumber = findHoleByConnectorKey(adjustableConnectorKey);
    return insertNumber === 0
        ? `${UPRIGHT_TO_INSERT}-${uprightNumber + 1}`
        : `${UPRIGHT_TO_INSERT}-${uprightNumber - 1}`;
};
